import { Injectable } from '@angular/core';

import { ChartCsvNamer }       from '../shared/components/charts/chart-csv-namer';
import { ChartFormatService }  from '../shared/components/charts/chart-format.service';
import { CSVService }          from '../core/services/csv.service';
import { IntelligenceService } from './intelligence.service';

const MAX_MATCHES = 10;

@Injectable({
  providedIn: 'root'
})
export class IntelligenceFormatService implements ChartCsvNamer {
  constructor (
    private chartFormatService : ChartFormatService,
    private csvService         : CSVService,
    private intelligenceService: IntelligenceService
  ) {}

  count ( value : number ) : string {
    return this.chartFormatService.count(value);
  }

  // the name carries the report and range, so downloads from different periods stay apart
  csvFileName ( name : string ) : string {
    const range = this.intelligenceService.range;

    return `intelligence_${ this.csvService.fileName(name) }_${ range.start_date }_${ range.end_date }`;
  }

  date ( value : string ) : string {
    const parsed = new Date(`${ value }T00:00:00`);

    return isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  downloadCsv ( name : string, rows : Array<any> ) : void {
    this.csvService.download(this.csvFileName(name), rows);
  }

  // carries the year, since a range boundary can sit in an earlier year than the rest
  fullDate ( value : string ) : string {
    const parsed = new Date(`${ value }T00:00:00`);

    return isNaN(parsed.getTime())
      ? value
      : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  hours ( value : number ) : string {
    return `${ ( value || 0 ).toFixed(1) }h`;
  }

  // a filter lives as long as the period still offers it, since options are rebuilt on every load
  keepIfOffered ( value : string, options : Array<string> ) : string {
    return value && ( options || [] ).indexOf(value) !== -1
      ? value
      : '';
  }

  // narrows an already loaded option list to those containing the typed text
  matchOptions ( options : Array<string>, query : string ) : Array<string> {
    const term = ( query || '' ).trim().toLowerCase();

    return ( options || [] )
      .filter(option => option.toLowerCase().indexOf(term) > -1)
      .slice(0, MAX_MATCHES);
  }

  money ( value : number ) : string {
    return this.chartFormatService.value(value, 'money');
  }

  // headline figures drop the cents
  moneyRounded ( value : number ) : string {
    return this.chartFormatService.headline(value, 'money');
  }

  percent ( value : number ) : string {
    return value === null || value === undefined
      ? '—'
      : `${ value.toFixed(1) }%`;
  }

  sortRows<RowType> ( rows : Array<RowType>, field : string, direction : string ) : Array<RowType> {
    const factor = direction === 'asc'
      ? 1
      : -1;
    const normalise = ( value : any ) : any => typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))
      ? Number(value)
      : value;

    return [ ...rows ].sort(( first, second ) => {
      const left = normalise(first[ field ]);
      const right = normalise(second[ field ]);

      if ( left === right ) {
        return 0;
      }

      if ( left === null || left === undefined ) {
        return 1;
      }

      if ( right === null || right === undefined ) {
        return -1;
      }

      return left > right
        ? factor
        : -factor;
    });
  }

  symbol () : string {
    return this.chartFormatService.symbol();
  }
}
